import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import '../../styles/CargoTab.css';

const CargoTab = ({ data, onChange }) => {
    const [cargoItems, setCargoItems] = useState(data || []);

    useEffect(() => {
        // Ensure we always have at least one row
        if (!data || data.length === 0) {
            const defaultRow = {
                CargoDescriptionID: null,
                Mark: '',
                Description: '',
                Weight: 0,
                Volume: 0,
                Packages: 0,
                GrossWeight: 0
            };
            setCargoItems([defaultRow]);
            onChange([defaultRow]);
        } else {
            setCargoItems(data);
        }
    }, [data]);

    const addNewRow = () => {
        const newRow = {
            CargoDescriptionID: null,
            Mark: '',
            Description: '',
            Weight: 0,
            Volume: 0,
            Packages: 0,
            GrossWeight: 0
        };
        const updated = [...cargoItems, newRow];
        setCargoItems(updated);
        onChange(updated);
    };

    const deleteRow = (index) => {
        if (cargoItems.length === 1) {
            alert('Cannot delete the last row. At least one cargo description is required.');
            return;
        }
        
        if (window.confirm('Delete this cargo description?')) {
            const updated = cargoItems.filter((_, i) => i !== index);
            setCargoItems(updated);
            onChange(updated);
        }
    };

    const updateRow = (index, field, value) => {
        const updated = [...cargoItems];
        updated[index][field] = value;
        
        // Auto-calculate GrossWeight if not manually entered
        if (['Weight', 'Packages'].includes(field) && field !== 'GrossWeight') {
            const weight = parseFloat(updated[index].Weight) || 0;
            const packages = parseFloat(updated[index].Packages) || 0;
            // GrossWeight = Weight * Packages (if applicable)
            // Or you can keep it independent - adjust logic as needed
        }
        
        setCargoItems(updated);
        onChange(updated);
    };

    const calculateTotals = () => {
        return cargoItems.reduce((acc, item) => ({
            Weight: acc.Weight + (parseFloat(item.Weight) || 0),
            Volume: acc.Volume + (parseFloat(item.Volume) || 0),
            Packages: acc.Packages + (parseFloat(item.Packages) || 0),
            GrossWeight: acc.GrossWeight + (parseFloat(item.GrossWeight) || 0)
        }), { Weight: 0, Volume: 0, Packages: 0, GrossWeight: 0 });
    };

    const totals = calculateTotals();

    return (
        <div className="cargo-tab">
            <div className="tab-header">
                <h3>Cargo Descriptions</h3>
                <button className="btn btn-primary btn-sm" onClick={addNewRow}>
                    <Plus size={16} />
                    Add Cargo Item
                </button>
            </div>

            <div className="table-wrapper">
                <table className="cargo-table">
                    <thead>
                        <tr>
                            <th style={{width: '50px'}}>#</th>
                            <th style={{width: '150px'}}>Mark</th>
                            <th style={{minWidth: '250px'}}>Description</th>
                            <th style={{width: '120px'}}>Weight (kg)</th>
                            <th style={{width: '120px'}}>Volume (CBM)</th>
                            <th style={{width: '100px'}}>Packages</th>
                            <th style={{width: '120px'}}>Gross Weight</th>
                            <th style={{width: '60px'}}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cargoItems.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="empty-message">
                                    No cargo items. Click "Add Cargo Item" to begin.
                                </td>
                            </tr>
                        ) : (
                            cargoItems.map((item, index) => (
                                <tr key={index}>
                                    <td className="row-number">{index + 1}</td>
                                    <td>
                                        <input
                                            type="text"
                                            value={item.Mark || ''}
                                            onChange={(e) => updateRow(index, 'Mark', e.target.value)}
                                            className="grid-input"
                                            placeholder="Mark"
                                        />
                                    </td>
                                    <td>
                                        <textarea
                                            value={item.Description || ''}
                                            onChange={(e) => updateRow(index, 'Description', e.target.value)}
                                            className="grid-textarea"
                                            placeholder="Enter cargo description"
                                            rows="2"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={item.Weight || ''}
                                            onChange={(e) => updateRow(index, 'Weight', parseFloat(e.target.value) || 0)}
                                            className="grid-input number"
                                            step="0.01"
                                            placeholder="0.00"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={item.Volume || ''}
                                            onChange={(e) => updateRow(index, 'Volume', parseFloat(e.target.value) || 0)}
                                            className="grid-input number"
                                            step="0.001"
                                            placeholder="0.000"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={item.Packages || ''}
                                            onChange={(e) => updateRow(index, 'Packages', parseFloat(e.target.value) || 0)}
                                            className="grid-input number"
                                            step="1"
                                            placeholder="0"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={item.GrossWeight || ''}
                                            onChange={(e) => updateRow(index, 'GrossWeight', parseFloat(e.target.value) || 0)}
                                            className="grid-input number"
                                            step="0.01"
                                            placeholder="0.00"
                                        />
                                    </td>
                                    <td>
                                        <button
                                            className="btn-icon btn-danger"
                                            onClick={() => deleteRow(index)}
                                            title="Delete"
                                            disabled={cargoItems.length === 1}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th colSpan="3">Total:</th>
                            <th className="total-cell">{totals.Weight.toFixed(2)}</th>
                            <th className="total-cell">{totals.Volume.toFixed(3)}</th>
                            <th className="total-cell">{totals.Packages}</th>
                            <th className="total-cell">{totals.GrossWeight.toFixed(2)}</th>
                            <th></th>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className="cargo-summary">
                <div className="summary-card">
                    <div className="summary-item">
                        <label>Total Weight:</label>
                        <span className="value">{totals.Weight.toFixed(2)} kg</span>
                    </div>
                    <div className="summary-item">
                        <label>Total Volume:</label>
                        <span className="value">{totals.Volume.toFixed(3)} CBM</span>
                    </div>
                    <div className="summary-item">
                        <label>Total Packages:</label>
                        <span className="value">{totals.Packages}</span>
                    </div>
                    <div className="summary-item">
                        <label>Total Gross Weight:</label>
                        <span className="value">{totals.GrossWeight.toFixed(2)} kg</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CargoTab;